import { useCallback, useEffect, useState } from "react";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Icon from "@mui/material/Icon";
import InputAdornment from "@mui/material/InputAdornment";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import type { SxProps, Theme } from "@mui/material/styles";
import { customerService, type Customer } from "../../services/customerService";
import { useTranslation } from "react-i18next";

export type CustomerSearchAutocompleteProps = {
  value: Customer | null;
  onChange: (customer: Customer | null) => void;
  label?: string;
  placeholder?: string;
  size?: "small" | "medium";
  fullWidth?: boolean;
  disabled?: boolean;
  sx?: SxProps<Theme>;
};

export default function CustomerSearchAutocomplete({
  value,
  onChange,
  label,
  placeholder,
  size = "small",
  fullWidth = true,
  disabled = false,
  sx,
}: CustomerSearchAutocompleteProps) {
  const { t } = useTranslation();
  const fieldLabel = label ?? t("form.customer");
  const fieldPlaceholder = placeholder ?? t("common.typeAtLeast2");
  const [options, setOptions] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState(value?.name ?? "");

  useEffect(() => {
    setInputValue(value?.name ?? "");
    if (value) {
      setOptions([value]);
    }
  }, [value]);

  const searchCustomers = useCallback(async (query: string) => {
    if (query.trim().length < 2) {
      setOptions(value ? [value] : []);
      return;
    }

    try {
      setLoading(true);
      const response = await customerService.getCustomers(0, 20, query, ["id", "name", "email"]);
      setOptions(response?.content ?? []);
    } catch (error) {
      console.error("Error searching customers:", error);
      setOptions(value ? [value] : []);
    } finally {
      setLoading(false);
    }
  }, [value]);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      const query = inputValue.trim();
      if (query.length >= 2 && query !== value?.name) {
        void searchCustomers(query);
      } else if (query.length < 2) {
        setOptions(value ? [value] : []);
      }
    }, 300);

    return () => window.clearTimeout(timeoutId);
  }, [inputValue, value, searchCustomers]);

  return (
    <Autocomplete
      sx={sx}
      fullWidth={fullWidth}
      disabled={disabled}
      size={size}
      options={options}
      loading={loading}
      filterOptions={(items) => items}
      getOptionLabel={(option) => option.name || option.id || ""}
      value={value}
      inputValue={inputValue}
      onInputChange={(_event, nextInputValue, reason) => {
        if (reason === "input") {
          setInputValue(nextInputValue);
          if (nextInputValue !== value?.name) {
            onChange(null);
          }
        } else if (reason === "clear") {
          setInputValue("");
          setOptions([]);
          onChange(null);
        } else if (reason === "reset") {
          setInputValue(value?.name ?? "");
        }
      }}
      onChange={(_event, customer) => {
        onChange(customer);
        setInputValue(customer?.name ?? "");
        setOptions(customer ? [customer] : []);
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={fieldLabel}
          placeholder={fieldPlaceholder}
          slotProps={{
            inputLabel: { shrink: true },
            input: {
              ...params.InputProps,
              startAdornment: (
                <>
                  <InputAdornment position="start">
                    <Icon fontSize="small" color="action">
                      business
                    </Icon>
                  </InputAdornment>
                  {params.InputProps.startAdornment}
                </>
              ),
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={16} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            },
          }}
        />
      )}
      renderOption={(props, option) => (
        <Box
          component="li"
          {...props}
          key={option.id}
          sx={{ display: "flex", alignItems: "center", gap: 1 }}
        >
          <Icon fontSize="small" color="action">
            business
          </Icon>
          <Box>
            <Typography variant="body2" fontWeight={600}>
              {option.name || t("common.unnamed")}
            </Typography>
            {option.email ? (
              <Typography variant="caption" color="text.secondary">
                {option.email}
              </Typography>
            ) : null}
          </Box>
        </Box>
      )}
      isOptionEqualToValue={(option, selected) => option.id === selected?.id}
      noOptionsText={
        inputValue.trim().length < 2 ? t("common.typeAtLeast2") : t("common.noResults")
      }
    />
  );
}
